import React, {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Badge,
  Box,
  Button,
  ButtonSelect,
  FooterActionBar,
  Icon,
  Loader,
  Menu,
  MenuItem,
  type MenuOption,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodAdvancedFilters,
  SodHeader,
  SodMessage,
} from '../../../shared/components';
import {
  AAG_SOD_VIOLATIONS,
  ABORT_ERROR,
  ADVANCED_FILTERS_METHODS,
  DEFAULT_AD_FILTER_PARAMS,
  testIds,
  VIOLATIONS_TAB_LIST_INITIAL_FILTERS,
} from '../../../shared/constants';
import { TABS_MAPPING } from '../../../shared/constants/violations';
import {
  useInvertedLocalize,
  useLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../shared/hooks';
import {
  type ActiveFiltersState,
  type AdvancedFilterState,
  type RootState,
} from '../../../shared/types';
import { createDevRejectionHandler } from '../../../shared/utils';
import { violationsTabActions } from '../../ViolationsTab/data-store/violationsTabSlice';
import {
  useFilterCache,
  useGetQueryMap,
  useTabFilters,
} from '../../ViolationsTab/hooks';
import { getFilterPayload, getListPayload } from '../../ViolationsTab/utils';
import {
  CURRENT_VIEW_TYPES,
  REPORT_MODAL_TABS,
  REPORT_TYPE_OPTIONS,
  TEMPLATES_TYPES,
} from '../data-layer/violationsReportConstants';
import { violationsReportActions } from '../data-store/violationsReportSlice';
import useExportReport from '../hooks/use-exportReport';
import { useReportColumnsPicker } from '../hooks/use-reportColumnsPicker';
import { useSodViolationsReportPreview } from '../hooks/use-sodViolationsReportPreview';
import { type ColumnConfig } from '../types';
import * as columnConfigUtils from '../utils/columnConfig';
import { buildFiltersMap } from '../utils/filters';

import ReportPreviewSearch from './ReportPreviewSearch';
import ReportTable from './ReportTable';
import SaveToMyReport from './SaveToMyReport';

const REPORT_SESSION_KEY = 'aag.report.session';

interface ReportSession {
  reportType: string;
  reportModalTab: string;
  reportTitle: string;
  activeFilters: ActiveFiltersState[];
  checkedFilterValues: string[];
}

const readReportSession = (): ReportSession | null => {
  try {
    const raw = sessionStorage.getItem(REPORT_SESSION_KEY);

    return raw ? (JSON.parse(raw) as ReportSession) : null;
  } catch {
    return null;
  }
};

interface ReportPreviewProps {
  setShowReportModal: Dispatch<SetStateAction<boolean>>;
}

/**
 * ReportPreviewContent - Internal component that uses the export context
 */

function ReportPreviewContent({
  setShowReportModal,
}: ReportPreviewProps): ReactElement {
  const dispatch = useDispatch();
  const { navigateToUrl } = useNavigateTo();
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isOpenSaveToMyReport, setIsOpenSaveToMyReport] = useState(false);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig>({
    selectedColumns: [],
    columnOrder: {},
  });
  const [pollPct, setPollPct] = useState(0);
  const [isExtendedLoading, setIsExtendedLoading] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const successTimerRef = useRef<NodeJS.Timeout | null>(null);

  const l = useInvertedLocalize();
  const localize = useLocalize();

  const { dataTheme } = useTypedSelector(
    (state: RootState) => state.aag.global
  );

  const {
    selectedReport,
    reportModalTab,
    isReportError,
    isLoading,
    exportProgress,
    activeExports,
    reportData,
    columnsByReportType,
  } = useTypedSelector((state: RootState) => state.aag.report);

  const {
    filters,
    activeFilters,
    filterCache,
    tabFilter,
    tabInAction,
    sortInAction,
    activeInsight,
  } = useTypedSelector((state) => state.aag.violationsTab.controls);
  const searchKey = useTypedSelector(
    (state) => state.aag.violationsTab.controls.searchKey
  );
  const [selection, setSelection] = useState(() => {
    const stored = readReportSession();

    return selectedReport || (stored?.reportType ?? 'summary');
  });

  const [sessionLoaded, setSessionLoaded] = useState(false);
  // Captures whether this is a browser refresh. On refresh Redux resets selectedReport to ''
  // (initial state); on normal in-app navigation it already has a value. Used to gate session
  // restore so we never overwrite live Redux state during normal navigation.
  const wasRefreshRef = useRef(!selectedReport);

  // One-time sync of selection with selectedReport on mount
  useEffect(() => {
    if (selectedReport && selectedReport !== selection) {
      setSelection(selectedReport);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only runs once on mount

  const reportKey = useMemo(
    () => (typeof selection === 'string' ? selection.toUpperCase() : ''),
    [selection]
  );

  // Calculate export progress for current report type only
  const currentExportProgress = exportProgress[reportKey] || 0;
  const exportProgressValue = useMemo(
    () => currentExportProgress || 0,
    [currentExportProgress]
  );

  // Check if there's an active export for current report type only
  const hasActiveExport = useMemo(
    () =>
      Array.isArray(activeExports) &&
      activeExports.some(
        (e: { reportType: string }) => e.reportType === reportKey
      ),
    [activeExports, reportKey]
  );

  // Filter management hooks
  const queryMap = useGetQueryMap();
  const { getFromCache, setCache } = useFilterCache(
    filterCache,
    violationsTabActions.setFiltersCache
  );
  const { initializeTabFilters } = useTabFilters();

  // For filter search functionality
  const abortRef = useRef<((reason: string) => void) | null>(null);
  const lastRequestedKeyRef = useRef<string | null>(null);

  const pageClasses = cx(
    'Sod-Report',
    isFilterExpanded && 'Sod-Report--isFilterExpanded'
  );

  const { initializeReportColumns } = useReportColumnsPicker(selection);
  const { getSodViolationsReportPreview } = useSodViolationsReportPreview();
  const { exportReport, isExporting } = useExportReport();

  // Fetch report columns whenever report type (selection) changes
  useEffect(() => {
    initializeReportColumns()
      .then(() => undefined)
      .catch(() => undefined);
  }, [initializeReportColumns]);

  // Handle column configuration changes from ReportTable
  const handleColumnConfigChange = useCallback((config: ColumnConfig) => {
    setColumnConfig(config);
  }, []);

  // Filter search handler
  const handleFilterSearch = useCallback(
    async <T extends string>(
      key: T,
      searchText: T
    ): Promise<MenuOption[] | undefined | false> => {
      const cacheResponse = getFromCache(key, searchText);

      if (cacheResponse) {
        return cacheResponse;
      }

      // Abort should only happen when the filter is same for search
      if (abortRef.current && lastRequestedKeyRef.current === key) {
        abortRef.current('New Search');
      }

      lastRequestedKeyRef.current = key;

      try {
        const fn = queryMap[key];

        const controller = fn({
          ...DEFAULT_AD_FILTER_PARAMS,
          search: searchText,
          key,
        });

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        const options = response as MenuOption[];

        setCache(key, searchText, options);

        return options;
      } catch (error) {
        const err = error as { name: string; message: string };

        if (err.name === ABORT_ERROR) {
          return false;
        }

        return undefined;
      }
    },
    [queryMap, getFromCache, setCache]
  );

  // Filter options click handler
  const handleOptionsClick = useCallback(
    (key: string, method: string, selectedOptions: MenuOption[]) => {
      if (selectedOptions.length === 0) {
        const newActiveFilters = activeFilters.filter(
          (filter) => filter.value !== key
        );

        dispatch(violationsTabActions.setActiveFilters(newActiveFilters));

        return;
      }

      const isFilterAvailable = activeFilters.find(
        (filter) => filter.value === key
      );
      const newActiveFilters = isFilterAvailable
        ? activeFilters.map((filter) => {
            if (filter.value === key) {
              return {
                ...filter,
                options: selectedOptions,
              };
            }

            return filter;
          })
        : [
            ...activeFilters,
            {
              value: key,
              method,
              options: selectedOptions,
            },
          ];

      dispatch(violationsTabActions.setActiveFilters(newActiveFilters));
    },
    [dispatch, activeFilters]
  );

  // Filter change handler
  const handleFilterChange = useCallback(
    (
      selectedFilter: Pick<
        AdvancedFilterState,
        'value' | 'isChecked' | 'method'
      >
    ) => {
      const isFilterApplied = activeFilters.find(
        (filter) => filter.value === selectedFilter.value
      );

      if (selectedFilter.isChecked && isFilterApplied) {
        handleOptionsClick(selectedFilter.value, selectedFilter.method, []);
      }

      const modifiedFilters = filters.map((filter) => {
        if (selectedFilter.value === filter.value) {
          return {
            ...filter,
            isChecked: !filter.isChecked,
          };
        }

        return filter;
      });

      dispatch(violationsTabActions.setAdvancedFilters(modifiedFilters));
    },
    [dispatch, filters, activeFilters, handleOptionsClick]
  );

  const handleClearBtn = useCallback(() => {
    dispatch(violationsTabActions.setActiveFilters([]));
  }, [dispatch]);

  const nonNullActiveFilters = useMemo(
    () => activeFilters.filter((f): f is ActiveFiltersState => Boolean(f)),
    [activeFilters]
  );

  // Build a single signature to coalesce multiple rapid Redux updates into one preview call
  const filtersSignature = useMemo(() => {
    try {
      const compact = nonNullActiveFilters
        .map((f) => ({
          v: f.value,
          m: f.method,
          o: f.options.map((o) => String(o.value)).sort(),
        }))
        .sort((a, b) => a.v.localeCompare(b.v));

      const sortSig = sortInAction
        ? `${sortInAction.value}|${sortInAction.order}`
        : '';

      return JSON.stringify({ compact, sortSig });
    } catch {
      return '[]';
    }
  }, [nonNullActiveFilters, sortInAction]);

  const previewSignature = useMemo(
    () =>
      JSON.stringify({
        selection,
        tabInAction,
        searchKey: searchKey ?? '',
        insight: activeInsight?.id ?? '',
        filtersSignature,
      }),
    [selection, tabInAction, searchKey, activeInsight, filtersSignature]
  );

  const lastSignatureRef = useRef<string | null>(null);

  const filtersMap = useMemo(
    () => buildFiltersMap(nonNullActiveFilters),
    [nonNullActiveFilters]
  );

  // Check if clear button should be shown
  const isClearBtn = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  // Create status filter as first filter in advanced filters
  const statusFilter: AdvancedFilterState = useMemo(() => {
    // Find active status filter to determine selected options
    const activeStatusFilter = activeFilters.find(
      (filter) => filter.value === 'status'
    );

    return {
      label: l('Status'),
      value: 'status',
      method: ADVANCED_FILTERS_METHODS.CUSTOM,
      isChecked: Boolean(activeStatusFilter), // Only checked if it exists in activeFilters
      category: l('Status'),
      options: tabFilter.map((tab) => ({
        label: l(tab.label),
        value: tab.value,
        isChecked: activeStatusFilter
          ? activeStatusFilter.options.some(
              (option) => option.value === tab.value
            )
          : false,
      })),
    };
  }, [tabFilter, activeFilters, l]);

  const [isColumnsAndFiltersLoaded, setColumnsAndFiltersLoaded] =
    useState(false);

  // Enhanced filters with status filter as first item
  const enhancedFilters = useMemo(() => {
    if (tabFilter.length === 0) {
      return filters;
    }

    return [statusFilter, ...filters];
  }, [statusFilter, filters, tabFilter.length]);

  useEffect(() => {
    const initializeColumnsAndFilters = async () => {
      const tasks: Promise<boolean>[] = [initializeReportColumns()];

      // Preserve applied filters coming from violations page; only init tab filters
      // if none currently exist
      if (!Array.isArray(activeFilters) || activeFilters.length === 0) {
        tasks.push(initializeTabFilters());
      }

      const response = await Promise.all(tasks);

      const isFalseValue = response.some((value) => !value);

      if (isFalseValue) {
        dispatch(violationsReportActions.setReportError({ state: true }));
      } else {
        setColumnsAndFiltersLoaded(true);
      }
    };

    initializeColumnsAndFilters().catch(
      createDevRejectionHandler('ReportPreview: initializeColumnsAndFilters')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Initialize status filter with current tabInAction when tabFilter is loaded
  useEffect(() => {
    if (tabFilter.length > 0 && tabInAction) {
      // Check if status filter already exists in activeFilters
      const hasStatusFilter = activeFilters.some(
        (filter) => filter.value === 'status'
      );

      // Only add status filter if it doesn't exist and we have a tabInAction
      if (!hasStatusFilter) {
        const currentTab = tabFilter.find((tab) => tab.value === tabInAction);

        if (currentTab) {
          const initialStatusFilter = {
            value: 'status',
            method: ADVANCED_FILTERS_METHODS.CUSTOM,
            options: [
              {
                label: currentTab.label,
                value: currentTab.value,
                isChecked: true,
              },
            ],
          };

          dispatch(
            violationsTabActions.setActiveFilters([
              initialStatusFilter,
              ...activeFilters,
            ])
          );
        }
      }
    }
  }, [tabFilter, tabInAction, activeFilters, dispatch]);

  // Restore persisted filters on browser refresh only.
  // wasRefreshRef distinguishes a fresh page load (selectedReport='' in Redux) from
  // normal in-app navigation (Redux already has a value). On normal navigation we must
  // NOT restore from session — the live Redux state from the Violations page is the
  // source of truth. sessionLoaded is state so setSessionLoaded(true) always triggers a
  // re-render, which causes the save effect to run and write the initial snapshot.
  useEffect(() => {
    if (!isColumnsAndFiltersLoaded || sessionLoaded) {
      return;
    }

    if (wasRefreshRef.current) {
      const stored = readReportSession();

      if (stored?.reportType === selection) {
        if (
          Array.isArray(stored.activeFilters) &&
          stored.activeFilters.length > 0
        ) {
          dispatch(violationsTabActions.setActiveFilters(stored.activeFilters));
        }

        if (
          Array.isArray(stored.checkedFilterValues) &&
          stored.checkedFilterValues.length > 0
        ) {
          const checkedSet = new Set(stored.checkedFilterValues);
          const restoredFilters = filters.map((f) => ({
            ...f,
            isChecked: checkedSet.has(f.value),
          }));

          dispatch(violationsTabActions.setAdvancedFilters(restoredFilters));
        }
      }
    }

    setSessionLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isColumnsAndFiltersLoaded]);

  // Sync local progress with API progress
  useEffect(() => {
    const apiProgress = exportProgress[reportKey] || 0;

    setPollPct(apiProgress);

    setIsExtendedLoading(apiProgress > 0 && apiProgress < 100);
  }, [exportProgress, reportKey]);

  // Stop polling when reaching 100 and mark success.
  useEffect(() => {
    if (!isExtendedLoading) {
      return;
    }

    if (pollPct < 100) {
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsExtendedLoading(false);
  }, [isExtendedLoading, pollPct]);

  // Auto-reset success after 5 seconds and clear export progress
  useEffect(() => {
    if (exportProgressValue >= 100) {
      successTimerRef.current = setTimeout(() => {
        dispatch(
          violationsReportActions.setExportProgress({
            reportType: reportKey,
            progressPercentage: 0,
          })
        );
      }, 3000);
    }

    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, [exportProgressValue, dispatch, reportKey]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
      }
    },
    []
  );

  const findTitle = useCallback(
    (report: string) => {
      const allReport = [...CURRENT_VIEW_TYPES, ...TEMPLATES_TYPES];

      const reportTitle =
        allReport.find((item) => item.value === report)?.title ?? '';

      let preText = '';

      if (reportModalTab === REPORT_MODAL_TABS.CURRENT_VIEW) {
        preText = l('Current View: ');
      } else if (reportModalTab === REPORT_MODAL_TABS.TEMPLATES) {
        preText = l('Template: ');
      } else if (reportModalTab === REPORT_MODAL_TABS.MY_REPORT) {
        preText = l('My Report: ');
      }

      return `${preText}${reportTitle ? l(reportTitle) : ''}`;
    },
    [reportModalTab, l]
  );

  // Manual preview trigger function
  const triggerPreview = useCallback(async () => {
    if (!isColumnsAndFiltersLoaded) {
      return;
    }

    // Use status filter values if available, otherwise fall back to tabInAction
    const statusValues: string[] = (() => {
      const sf = nonNullActiveFilters.find((f) => f.value === 'status');

      return sf ? sf.options.map((o) => o.value) : [];
    })();

    const effectiveTabInAction =
      statusValues.length > 0 ? statusValues[0] : tabInAction;

    const payload = getListPayload(
      filtersMap,
      effectiveTabInAction ?? undefined,
      searchKey,
      sortInAction,
      activeInsight,
      { max: 50, offset: 1 }
    );

    // Override sodStatuses in the payload if we have status filter selections
    if (statusValues.length > 0) {
      const mappedStatusValues = statusValues.map(
        (status) => TABS_MAPPING[status] || status
      );

      payload.filters.sodStatuses = mappedStatusValues;
    }

    await getSodViolationsReportPreview(payload, selection.toUpperCase());
  }, [
    isColumnsAndFiltersLoaded,
    nonNullActiveFilters,
    filtersMap,
    tabInAction,
    sortInAction,
    activeInsight,
    selection,
    getSodViolationsReportPreview,
    searchKey,
  ]);

  const typeButtonLabel = l(
    REPORT_TYPE_OPTIONS.find((option) => option.value === selection)?.label ??
      'Summary'
  );

  const applySelection = (value: 'summary' | 'detail' | 'permissions') => {
    setSelection(value);
    dispatch(violationsReportActions.setSelectedReport(value));
    setReportTitle(findTitle(value));
    // On type change: retain the current Status filter (represents the active tab/status)
    // but reset all other active filters and restore default chip visibility.
    const currentStatusFilter = activeFilters.find((f) => f.value === 'status');

    dispatch(
      violationsTabActions.setActiveFilters(
        currentStatusFilter ? [currentStatusFilter] : []
      )
    );
    dispatch(
      violationsTabActions.setAdvancedFilters(
        VIOLATIONS_TAB_LIST_INITIAL_FILTERS.map((f) => ({ ...f }))
      )
    );
  };

  const getHeaderMenu = () => (
    <Box tag='div' className='ReportHeaderMenu--items'>
      <MenuItem
        option={{
          label: l('Summary'),
          value: 'summary',
          secondaryLabel: l('Lists each user violation as a single record.'),
        }}
        onClick={() => applySelection('summary')}
        suffixIcon={selection === 'summary' ? <Icon kind='Check' /> : <Box />}
      />
      <MenuItem
        option={{
          label: l('Detail'),
          value: 'detail',
          secondaryLabel: l('Includes assigned entitlements and roles.'),
        }}
        onClick={() => applySelection('detail')}
        suffixIcon={selection === 'detail' ? <Icon kind='Check' /> : <Box />}
      />
      <MenuItem
        option={{
          label: l('Permissions'),
          value: 'permissions',
          secondaryLabel: l(
            'Includes transaction codes that contribute to violations.'
          ),
        }}
        onClick={() => applySelection('permissions')}
        suffixIcon={
          selection === 'permissions' ? <Icon kind='Check' /> : <Box />
        }
      />
    </Box>
  );

  const typeSelectLabel = `${l('Type')}: ${typeButtonLabel}`;

  // Single coalesced preview trigger based on a unified signature.
  // sessionLoaded guard prevents a double-call on browser refresh: the restore effect
  // dispatches setActiveFilters in the same render that isColumnsAndFiltersLoaded becomes
  // true, so previewSignature would fire once with stale filters and again after Redux
  // updates. Waiting for sessionLoaded ensures filters are restored before the first call.
  useEffect(() => {
    if (!isColumnsAndFiltersLoaded || !sessionLoaded) {
      return;
    }

    if (lastSignatureRef.current === previewSignature) {
      return;
    }

    lastSignatureRef.current = previewSignature;
    triggerPreview().catch(
      createDevRejectionHandler('ReportPreview: triggerPreview')
    );
  }, [
    isColumnsAndFiltersLoaded,
    sessionLoaded,
    previewSignature,
    triggerPreview,
  ]);

  const renderedHeaderControls = (
    <Box className='Sod-Report-headerControls'>
      {reportModalTab === REPORT_MODAL_TABS.CURRENT_VIEW ? (
        <Menu
          CustomMenu={getHeaderMenu()}
          trigger={
            <ButtonSelect
              kind='rectangle'
              label={typeSelectLabel}
              ariaLabel={l('Select report type')}
            />
          }
          placement='bottom-end'
          shouldCloseMenuOnItemClick
        />
      ) : null}

      <Box>
        <ButtonSelect
          label={l('Filter')}
          ariaLabel={l('Filter')}
          kind='rectangle'
          onClick={() => {
            setIsFilterExpanded(!isFilterExpanded);
          }}
          isOpen={isFilterExpanded}
          prefixIcon={<Icon kind='Filter' />}
          BadgeComp={
            isClearBtn ? (
              <Badge kind='dot' color='primary' size='small' />
            ) : null
          }
        />
      </Box>

      <ReportPreviewSearch />
    </Box>
  );

  const renderedFilters = (
    <SodAdvancedFilters
      filters={enhancedFilters}
      activeFilters={activeFilters}
      isClearBtn={isClearBtn}
      searchDebounce={400}
      handleFilterChange={handleFilterChange}
      handleFilterSearch={handleFilterSearch}
      handleOptionsClick={handleOptionsClick}
      handleClearBtn={handleClearBtn}
    />
  );

  const handleExport = async () => {
    // Use status filter values if available, otherwise fall back to tabInAction
    const statusValues: string[] = (() => {
      const sf = nonNullActiveFilters.find((f) => f.value === 'status');

      return sf ? sf.options.map((o) => o.value) : [];
    })();

    const effectiveTabInAction =
      statusValues.length > 0 ? statusValues[0] : tabInAction;

    const reportTypeUpper = selection.toUpperCase() as
      | 'DETAIL'
      | 'SUMMARY'
      | 'PERMISSIONS';

    const columnsData = columnsByReportType?.[reportTypeUpper];
    const columnDisplay = columnConfigUtils.buildColumnDisplay(
      columnConfig.selectedColumns,
      columnsData,
      localize
    );

    // Use dynamic column configuration from ReportTable
    const payload = {
      reportType: reportTypeUpper,
      format: 'EXCEL' as const,
      locale: 'en' as const,
      filters: getFilterPayload(
        filtersMap,
        effectiveTabInAction ?? undefined,
        searchKey ?? null
      ),
      columnConfig: {
        columnOrder: columnConfig.columnOrder,
        selectedColumns: columnConfig.selectedColumns,
        columnDisplay,
      },
      formattingOptions: {
        excelOptions: {
          cellFormatting: {
            data: {
              dateFormat: 'yyyy-MM-dd HH:mm:ss',
              numberFormat: '#,##0.00',
            },
          },
        },
      },
      fileName: `SOD_Violations_Report_${new Date().getFullYear()}`,
      timezone: 'UTC',
    };

    // Override sodStatuses in the payload if we have status filter selections
    if (statusValues.length > 0) {
      payload.filters.sodStatuses = statusValues.map(
        (status) => TABS_MAPPING[status] || status
      );
    }

    await exportReport(payload);
  };

  const [reportTitle, setReportTitle] = useState(() => {
    const computed = findTitle(selectedReport);

    if (computed) {
      return computed;
    }

    // On refresh selectedReport='' so findTitle returns ''; fall back to stored title
    const stored = readReportSession();

    return stored?.reportTitle ?? '';
  });

  // Persist full session to sessionStorage whenever any relevant state changes.
  // sessionLoaded in the dep array guarantees one write immediately after the restore/init
  // phase completes, capturing the initial snapshot even when no Redux dispatch was needed.
  // No isNewReportType guard needed: applySelection and the sync effect above always reset
  // Redux to the correct default state before the save fires, so we simply always persist
  // the current Redux values.
  // Must appear after reportTitle declaration to avoid temporal dead zone error.
  useEffect(() => {
    if (!sessionLoaded) {
      return;
    }

    sessionStorage.setItem(
      REPORT_SESSION_KEY,
      JSON.stringify({
        reportType: selection,
        reportModalTab,
        reportTitle,
        activeFilters,
        checkedFilterValues: filters
          .filter((f) => f.isChecked)
          .map((f) => f.value),
      })
    );
  }, [
    sessionLoaded,
    selection,
    activeFilters,
    filters,
    reportModalTab,
    reportTitle,
  ]);

  // Coalesced preview trigger above replaces this effect

  // Sync selection when selectedReport changes via Preview button or View All Reports.
  // Guard against empty string: on refresh Redux resets to '' before restoration fires.
  useEffect(() => {
    if (selectedReport && selectedReport !== selection) {
      setSelection(selectedReport);
      setReportTitle(findTitle(selectedReport));
      // On type change: retain the current Status filter, reset everything else to defaults.
      const currentStatusFilter = activeFilters.find(
        (f) => f.value === 'status'
      );

      dispatch(
        violationsTabActions.setActiveFilters(
          currentStatusFilter ? [currentStatusFilter] : []
        )
      );
      dispatch(
        violationsTabActions.setAdvancedFilters(
          VIOLATIONS_TAB_LIST_INITIAL_FILTERS.map((f) => ({ ...f }))
        )
      );
    }
  }, [selectedReport, selection, findTitle, dispatch, activeFilters]);

  // Reset export success state after showing it briefly
  useEffect(() => {
    if (exportProgressValue >= 100 && !hasActiveExport) {
      const timer = setTimeout(() => {
        // This will trigger a re-render and hide the success state
        // The actual reset should be handled by the parent component/Redux
      }, 3000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [exportProgressValue, hasActiveExport]);

  const headerParams = {
    title: reportTitle,
    breadcrumbItems: [
      { text: l('Violations'), href: AAG_SOD_VIOLATIONS },
      {
        text: l('Report'),
        onClick: () => setShowReportModal(true),
      },
    ],
  };

  const renderHeader = () => (
    <SodHeader
      title={headerParams.title}
      controls={renderedHeaderControls}
      className='Sod-Report-header'
      breadcrumbItems={headerParams.breadcrumbItems}
      handleGoBack={() => navigateToUrl(AAG_SOD_VIOLATIONS)}
    />
  );

  const renderBody = () => (
    <article className='Sod-Report-content Sod-Vio-Report-contentWrapper'>
      {isFilterExpanded && (
        <Box className='Sod-Report-filtersBlock'>{renderedFilters}</Box>
      )}

      {isReportError && (
        <Box tag='div' className='Sod-Report-errorContainer'>
          <Typography>
            {l('Error loading report data, Please try again later.')}
          </Typography>
        </Box>
      )}

      {!isReportError && isLoading && (
        <Box className='Sod-Report-noRecordFound'>
          <Loader kind='loop' format='main' color='brand' />
        </Box>
      )}

      {!isReportError && !isLoading && (
        <>
          {Array.isArray(reportData) && reportData.length === 0 ? (
            <Box className='Sod-Report-noRecordFound'>
              <SodMessage
                isSearchActive={false}
                isFilterActive={false}
                handleFilterClearBtn={handleClearBtn}
                defaultTitle={l('No Records')}
                defaultMessage={l(
                  'No Record Found. Change some filters and try again.'
                )}
              />
            </Box>
          ) : (
            <ReportTable onColumnConfigChange={handleColumnConfigChange} />
          )}
          <SaveToMyReport
            isOpen={isOpenSaveToMyReport}
            setIsOpen={setIsOpenSaveToMyReport}
          />
        </>
      )}
    </article>
  );

  const renderFooter = () => (
    <FooterActionBar className='Sod-Report-footerActionBar'>
      <Button
        type='button'
        kind='subtle'
        onClick={() => setShowReportModal(true)}
        isDisabled={isLoading || isExporting}>
        {l('View All Reports')}
      </Button>
      <Box className='Sod-Report-footerGroupRight'>
        {/* Once this is supported uncomment here */}
        {/* <Button
          type='button'
          kind='outlined'
          onClick={() => setIsOpenSaveToMyReport(true)}>
          {l('Save to My Report')}
        </Button> */}
        {hasActiveExport ||
        (exportProgressValue > 0 && exportProgressValue < 100) ||
        exportProgressValue >= 100 ? (
          <Tooltip
            text={l('An export is already in progress for this report type')}
            placement='top'
            isPortalEnabled
            trigger={
              <Button
                key={`export-progress-${reportKey}`}
                kind='filled'
                size='medium'
                isExtendedLoading={
                  hasActiveExport ||
                  (exportProgressValue > 0 && exportProgressValue < 100)
                }
                isSuccess={exportProgressValue >= 100}
                extendedLoadingPercentValue={exportProgressValue}
                onClick={handleExport}
                loaderText={`${l('Exporting')} (${exportProgressValue}%)`}
                leftIcon={
                  exportProgressValue >= 100 ? <Icon kind='Check' /> : undefined
                }
                successText={l('Exported')}
                className={cx(
                  (hasActiveExport ||
                    (exportProgressValue > 0 && exportProgressValue < 100)) &&
                    'Sod-Report-Exporting-button Button--isExtendedLoading'
                )}
                isDisabled={
                  isLoading ||
                  !(Array.isArray(reportData) && reportData.length > 0)
                }>
                {l('Export')}
              </Button>
            }
          />
        ) : (
          <Button
            key={`export-normal-${reportKey}`}
            type='button'
            kind='filled'
            onClick={handleExport}
            isDisabled={
              isLoading ||
              isExporting ||
              !(Array.isArray(reportData) && reportData.length > 0)
            }>
            {isExporting ? l('Exporting...') : l('Export')}
          </Button>
        )}
        {/* <ReportActions isReportSavedAndThenChanged /> */}
      </Box>
    </FooterActionBar>
  );

  return (
    <Box
      tag='div'
      data-theme={dataTheme}
      className={pageClasses}
      data-testid={testIds.SOD_REPORT_PAGE}>
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </Box>
  );
}

/**
 * ReportPreview
 */
function ReportPreview({
  setShowReportModal,
}: ReportPreviewProps): ReactElement {
  return <ReportPreviewContent setShowReportModal={setShowReportModal} />;
}

export default ReportPreview;
